/**
 * DonationWidget - Support our research component
 *
 * Features:
 * - One-time donations: $5, $25, $100, Custom
 * - Recurring support: $5/mo, $19/mo
 * - Stripe checkout integration
 */

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Heart, Coffee, Rocket, Star, Loader2, Check } from 'lucide-react'
import { publicFetch } from '@/services/api'

interface DonationWidgetProps {
  variant?: 'inline' | 'compact'
}

interface AmountOption {
  cents: number
  label: string
  icon: React.ReactNode
  description?: string
}

const oneTimeAmounts: AmountOption[] = [
  { cents: 500, label: '$5', icon: <Coffee size={18} />, description: 'Buy us a coffee' },
  { cents: 2500, label: '$25', icon: <Heart size={18} />, description: 'Support research' },
  { cents: 10000, label: '$100', icon: <Rocket size={18} />, description: 'Sponsor a report' },
]

const recurringAmounts: AmountOption[] = [
  { cents: 500, label: '$5/mo', icon: <Heart size={18} />, description: 'Monthly supporter' },
  { cents: 1900, label: '$19/mo', icon: <Star size={18} />, description: 'Research patron' },
]

export function DonationWidget({ variant = 'inline' }: DonationWidgetProps) {
  const [selectedAmount, setSelectedAmount] = useState<number>(2500)
  const [customAmount, setCustomAmount] = useState('')
  const [isRecurring, setIsRecurring] = useState(false)
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [error, setError] = useState('')

  const getAmountCents = (): number => {
    if (selectedAmount === -1 && customAmount) {
      const parsed = parseFloat(customAmount)
      return isNaN(parsed) ? 0 : Math.round(parsed * 100)
    }
    return selectedAmount
  }

  const handleDonate = async () => {
    const amountCents = getAmountCents()

    if (amountCents < 100) {
      setError('Minimum donation is $1')
      setStatus('error')
      return
    }

    setStatus('loading')
    setError('')

    try {
      const response = await publicFetch('/api/donate/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_cents: amountCents,
          recurring: isRecurring,
          email: email || undefined,
          success_url: `${window.location.origin}${window.location.pathname}?donated=true`,
          cancel_url: `${window.location.origin}${window.location.pathname}`,
        }),
      })

      const data = await response.json()

      if (data.checkout_url) {
        window.location.href = data.checkout_url
      } else {
        setError(data.error || 'Failed to create checkout')
        setStatus('error')
      }
    } catch {
      setError('Failed to process donation')
      setStatus('error')
    }
  }

  // Check if we just completed a donation
  const urlParams = new URLSearchParams(window.location.search)
  const justDonated = urlParams.get('donated') === 'true'

  if (justDonated) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        style={{
          padding: '2rem',
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '16px',
          textAlign: 'center',
        }}
      >
        <Check size={48} color="#22c55e" style={{ marginBottom: '1rem' }} />
        <h3 style={{ color: '#22c55e', marginBottom: '0.5rem' }}>Thank You!</h3>
        <p style={{ color: '#9ca3af' }}>Your support helps us continue writing about AI.</p>
      </motion.div>
    )
  }

  // Compact variant
  if (variant === 'compact') {
    return (
      <div style={{
        padding: '1rem',
        background: '#fafafa',
        border: '1px solid #eaeaea',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
      }}>
        <Heart size={24} color="#ec4899" />
        <div style={{ flex: 1 }}>
          <div style={{ color: '#0f0f0f', fontWeight: 500, fontSize: '0.9rem' }}>Support The Human+AI Project</div>
          <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Help humans thrive with AI</div>
        </div>
        <motion.button
          onClick={() => {
            setSelectedAmount(500)
            handleDonate()
          }}
          disabled={status === 'loading'}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{
            padding: '0.5rem 1rem',
            background: '#111111',
            border: 'none',
            borderRadius: '8px',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >
          {status === 'loading' ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : 'Donate'}
        </motion.button>
      </div>
    )
  }

  // Full inline variant
  return (
    <div style={{
      padding: '1.5rem',
      background: '#fafafa',
      border: '1px solid #eaeaea',
      borderRadius: '16px',
      marginTop: '2rem',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '1rem',
      }}>
        <Heart size={20} color="#ec4899" />
        <span style={{ color: '#0f0f0f', fontWeight: 600 }}>Support The Human+AI Project</span>
      </div>

      <p style={{
        color: '#9ca3af',
        fontSize: '0.9rem',
        marginBottom: '1.25rem',
      }}>
        Your support helps us build tools and insights that help humans thrive alongside AI.
      </p>

      {/* One-time / Recurring toggle */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1rem',
      }}>
        <motion.button
          onClick={() => setIsRecurring(false)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{
            flex: 1,
            padding: '0.5rem',
            background: !isRecurring ? 'rgba(99, 102, 241, 0.10)' : '#fafafa',
            border: `1px solid ${!isRecurring ? '#6366f1' : '#eaeaea'}`,
            borderRadius: '8px',
            color: !isRecurring ? 'white' : '#9ca3af',
            fontWeight: 500,
            fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >
          One-time
        </motion.button>
        <motion.button
          onClick={() => setIsRecurring(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{
            flex: 1,
            padding: '0.5rem',
            background: isRecurring ? 'rgba(99, 102, 241, 0.10)' : '#fafafa',
            border: `1px solid ${isRecurring ? '#6366f1' : '#eaeaea'}`,
            borderRadius: '8px',
            color: isRecurring ? 'white' : '#9ca3af',
            fontWeight: 500,
            fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >
          Monthly
        </motion.button>
      </div>

      {/* Amount selection */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.5rem',
        marginBottom: '1rem',
      }}>
        {(isRecurring ? recurringAmounts : oneTimeAmounts).map((option) => (
          <motion.button
            key={option.cents}
            onClick={() => {
              setSelectedAmount(option.cents)
              setCustomAmount('')
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              padding: '0.75rem',
              background: selectedAmount === option.cents
                ? 'rgba(236, 72, 153, 0.10)'
                : '#fafafa',
              border: `1px solid ${selectedAmount === option.cents ? '#ec4899' : '#eaeaea'}`,
              borderRadius: '10px',
              cursor: 'pointer',
              textAlign: 'center' as const,
            }}
          >
            <div style={{ color: '#ec4899', marginBottom: '0.25rem' }}>{option.icon}</div>
            <div style={{ color: '#0f0f0f', fontWeight: 600, fontSize: '1rem' }}>{option.label}</div>
            <div style={{ color: '#9ca3af', fontSize: '0.7rem' }}>{option.description}</div>
          </motion.button>
        ))}
      </div>

      {/* Custom amount (one-time only) */}
      {!isRecurring && (
        <div style={{ marginBottom: '1rem' }}>
          <div
            onClick={() => setSelectedAmount(-1)}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.75rem',
              background: selectedAmount === -1 ? 'rgba(236, 72, 153, 0.10)' : '#fafafa',
              border: `1px solid ${selectedAmount === -1 ? '#ec4899' : '#eaeaea'}`,
              borderRadius: '10px',
              cursor: 'pointer',
            }}
          >
            <span style={{ color: '#9ca3af', marginRight: '0.5rem' }}>$</span>
            <input
              type="number"
              value={customAmount}
              onChange={(e) => {
                setCustomAmount(e.target.value)
                setSelectedAmount(-1)
              }}
              placeholder="Custom amount"
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                color: '#0f0f0f',
                fontSize: '1rem',
                outline: 'none',
              }}
            />
          </div>
        </div>
      )}

      {/* Email (optional) */}
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email for receipt (optional)"
        style={{
          width: '100%',
          padding: '0.75rem',
          background: '#fafafa',
          border: '1px solid #eaeaea',
          borderRadius: '8px',
          color: '#0f0f0f',
          fontSize: '0.9rem',
          outline: 'none',
          marginBottom: '1rem',
          boxSizing: 'border-box' as const,
        }}
      />

      {/* Donate button */}
      <motion.button
        onClick={handleDonate}
        disabled={status === 'loading'}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        style={{
          width: '100%',
          padding: '1rem',
          background: '#111111',
          border: 'none',
          borderRadius: '10px',
          color: '#ffffff',
          fontWeight: 600,
          fontSize: '1rem',
          cursor: status === 'loading' ? 'wait' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          
        }}
      >
        {status === 'loading' ? (
          <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        ) : (
          <>
            <Heart size={18} />
            Donate {selectedAmount === -1 && customAmount ? `$${customAmount}` : (isRecurring ? recurringAmounts : oneTimeAmounts).find(o => o.cents === selectedAmount)?.label || ''}
          </>
        )}
      </motion.button>

      {/* Error message */}
      <AnimatePresence>
        {status === 'error' && error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'rgba(239, 68, 68, 0.1)',
              borderRadius: '8px',
              color: '#ef4444',
              fontSize: '0.85rem',
              textAlign: 'center',
            }}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Security note */}
      <div style={{
        marginTop: '1rem',
        textAlign: 'center',
        color: '#6b7280',
        fontSize: '0.75rem',
      }}>
        Secure payment powered by Stripe
      </div>
    </div>
  )
}
